/* eslint-disable no-restricted-imports */
import React, { FC, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { addHideTabbarParam } from '../../../routing/post/utils/addHideTabbarParam';
import { buildImageSource, buildImageSourceSet } from '../../../utils/buildImageSource';
import { formatCurrency } from '../../../utils/format/formatCurrency';
import { useTranslation } from '../../../utils/useTranslation';
import { IconProduct } from '../../icon/IconProduct';
import { Button } from '../../input/Button';
import { Flex } from '../../layout/Flex';
import { Image } from '../../media/Image';
import { MediaCover } from '../../media/MediaCover';
import { Typography } from '../../typography/Typography';
import { PostBusinessInfo } from '../PostBusinessInfo/PostBusinessInfo';
import { PostProductsFixedContainer } from '../PostProductsFixedContainer/PostProductsFixedContainer';
import { ProductMonetization } from '../ProductMonetization/ProductMonetization';
import { PostSingleProductProps } from './types';

export const PostSingleProduct: FC<PostSingleProductProps> = ({
    product,
    business,
    onBuyOnSiteClick,
    onCatalogClick,
    hideTabbar,
    businessButtonTitle,
    onBusinessClick,
    trackProductShown,
    trackButtonShown,
}) => {
    const t = useTranslation();
    const { priceInfo, oldPriceInfo, content, title, url, id, monetizationEnabled } = product;

    const media = content[0]?.media;

    const integerPart = priceInfo?.integerPart;
    const fractionalPart = priceInfo?.fractionalPart;
    const hasPriceInfo = integerPart !== undefined && integerPart !== null;

    const [hasProductLogged, setHasProductLogged] = useState(false);
    const [hasBusinessLogged, setHasBusinessLogged] = useState(false);

    useEffect(() => {
        if (!hasProductLogged && product) {
            product?.id && trackProductShown?.(product.id, product.url);
            product?.url && trackButtonShown?.('BuyOnSite', product.url);
            setHasProductLogged(true);
        }
    }, [hasProductLogged, product, trackButtonShown, trackProductShown]);

    useEffect(() => {
        if (!hasBusinessLogged && business) {
            business?.siteUrl && trackButtonShown?.('GoToSite', business.siteUrl);
            business?.catalogUrl && trackButtonShown?.('Catalog', business.catalogUrl);
            setHasBusinessLogged(true);
        }
    }, [business, hasBusinessLogged, trackButtonShown]);

    return (
        <Flex direction="column" width="100%" height="100%">
            <MediaCover
                minHeight={200}
                ratio={1}
                overflow="hidden"
                width="100%"
                borderTopLeftRadius={3}
                borderTopRightRadius={3}
            >
                <Flex
                    alignItems="center"
                    justifyContent="center"
                    backgroundColor="var(--background-primary)"
                >
                    {media ? (
                        <Image
                            objectFit="contain"
                            width="100%"
                            height="100%"
                            src={buildImageSource(media)}
                            srcSet={buildImageSourceSet(media)}
                        />
                    ) : (
                        <IconProduct size={72} color="var(--control-secondary-background)" />
                    )}
                </Flex>
            </MediaCover>
            <Flex p={2} pb={0} width="100%">
                {monetizationEnabled ? <ProductMonetization color="invert" pb={1} /> : null}
                <Typography variant="body" weight="regular" pb={1}>
                    {t(title)}
                </Typography>
                {hasPriceInfo ? (
                    <Flex direction="row" alignItems="baseline">
                        <Typography
                            weight="bold"
                            variant="title3"
                            color="var(--control-primary-background)"
                            textTransform="none"
                        >
                            {formatCurrency(integerPart, fractionalPart)}
                        </Typography>
                        {oldPriceInfo?.integerPart ? (
                            <Typography
                                variant="body"
                                color="var(--control-disabled-color)"
                                textTransform="none"
                                textDecoration="line-through"
                                ml={1}
                            >
                                {formatCurrency(
                                    oldPriceInfo.integerPart,
                                    oldPriceInfo.fractionalPart,
                                )}
                            </Typography>
                        ) : null}
                    </Flex>
                ) : null}
                {business?.siteUrl ? (
                    <PostBusinessInfo
                        business={business}
                        businessButtonTitle={businessButtonTitle || t('Каталог')}
                        onBusinessClick={onBusinessClick}
                    />
                ) : null}
            </Flex>
            <PostProductsFixedContainer p={2} mt="auto">
                <Flex width="100%" spacing={1}>
                    {business?.catalogUrl ? (
                        <Button
                            size="l"
                            as={Link}
                            to={
                                hideTabbar
                                    ? addHideTabbarParam(business.catalogUrl)
                                    : business.catalogUrl
                            }
                            width="100%"
                            color="secondary"
                            spacing={0.5}
                            onClick={onCatalogClick}
                        >
                            <IconProduct fontSize={20} />
                            {t(business.catalogTitle)}
                        </Button>
                    ) : null}
                    <Button
                        as="a"
                        href={url}
                        target="_blank"
                        size="l"
                        width="100%"
                        color="primary"
                        onClick={() => onBuyOnSiteClick(id, url)}
                    >
                        {t('Купить на сайте')}
                    </Button>
                </Flex>
            </PostProductsFixedContainer>
        </Flex>
    );
};
